if (!customElements.get('story-telling')) {
  class StoryTelling extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.mediaItems = Array.from(this.querySelectorAll('[data-media-index]'));

      if (!this.tablist || this.tabs.length === 0) return;

      this.activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];

      this.tablist.addEventListener('click', this.handleClick.bind(this));
      this.tablist.addEventListener('keydown', this.handleKeydown.bind(this));

      // Sync panels/media with the initially active tab (also starts any autoplay video).
      this.activateTab(this.activeTab, false);

      // Theme editor: activate the tab whose block is being edited.
      this.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
    }

    /**
     * Activates the clicked tab.
     * @param {MouseEvent} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab || tab === this.activeTab) return;
      this.activateTab(tab);
    }

    /**
     * Provides arrow/Home/End keyboard navigation across the tablist.
     * @param {KeyboardEvent} evt - Event object.
     */
    handleKeydown(evt) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(evt.key)) return;
      evt.preventDefault();

      const lastIndex = this.tabs.length - 1;
      const currentIndex = this.tabs.indexOf(this.activeTab);
      let nextIndex = currentIndex;

      if (evt.key === 'ArrowRight') nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      else if (evt.key === 'ArrowLeft') nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
      else if (evt.key === 'Home') nextIndex = 0;
      else if (evt.key === 'End') nextIndex = lastIndex;

      this.activateTab(this.tabs[nextIndex]);
    }

    /**
     * Activates the tab matching a block selected in the theme editor.
     * @param {CustomEvent} evt - Event object.
     */
    handleBlockSelect(evt) {
      const tab = evt.target.closest ? evt.target.closest('[role="tab"]') : null;
      if (tab) this.activateTab(tab, false);
    }

    /**
     * Activates a tab and reveals its panel + media, pausing inactive videos.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} [focus=true] - Move focus to the activated tab.
     */
    activateTab(tab, focus = true) {
      const index = this.tabs.indexOf(tab);
      if (index === -1) return;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) item.removeAttribute('tabindex');
        else item.setAttribute('tabindex', '-1');
      });

      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      this.mediaItems.forEach((item, i) => {
        const active = i === index;
        item.hidden = !active;

        const video = item.querySelector('video');
        if (!video) return;

        if (active && item.dataset.autoplay === 'true') {
          const playback = video.play();
          if (playback) playback.catch(() => { /* Ignore autoplay rejection (e.g. low power mode). */ });
        } else {
          video.pause();
        }
      });

      this.activeTab = tab;
      if (focus) tab.focus();
    }
  }

  customElements.define('story-telling', StoryTelling);
}
